import React, {useEffect, useState} from "react";
import {Form, Input, Button, Card, Typography, Divider, Space, Row, Col} from "antd";
import api from "../../api";
import {DatePicker, message} from "antd";
import dayjs from "dayjs";
import {NoWrap} from "../../styles/styles";

const {Title, Text} = Typography;

const UserProfilePage: React.FC = () => {
    const [form] = Form.useForm();
    const [passwordForm] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [justSaved, setJustSaved] = useState(false);
    const [passwordSaved, setPasswordSaved] = useState(false);
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [passwordError, setPasswordError] = useState("");

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await api.get("/users/me/");
                const data = res.data;
                if (data.birthday) {
                    data.birthday = dayjs(data.birthday);
                }
                form.setFieldsValue(data);
            } catch {
                message.error("Failed to load profile");
            }
        };

        fetchProfile();
    }, [form]);

    const handleProfileUpdate = async (values: any) => {
        setLoading(true);
        try {
            const payload = {
                ...values,
                birthday: values.birthday
                    ? dayjs.isDayjs(values.birthday)
                        ? values.birthday.format("YYYY-MM-DD")
                        : dayjs(values.birthday).format("YYYY-MM-DD")
                    : null,
            };

            await api.patch("/users/me/", payload);
            message.success("Profile updated successfully");
            setJustSaved(true);
            setTimeout(() => setJustSaved(false), 3000);
        } catch {
            message.error("Failed to update profile");
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = async (values: any) => {
        setPasswordLoading(true);
        try {
            await api.post("/users/change_password/", {
                old_password: values.current_password,
                new_password: values.new_password,
            });
            passwordForm.resetFields();
            setPasswordSaved(true);
            setTimeout(() => setPasswordSaved(false), 3000);
        } catch (err: any) {
            const msg =
                err.response?.data?.error ||
                err.response?.data?.detail ||
                "❌ Failed to change password";

            passwordForm.setFields([
                {
                    name: "current_password",
                    errors: [msg],
                },
            ]);
        } finally {
            setPasswordLoading(false);
        }
    };


    return (
        <div style={{maxWidth: 600, margin: "0 auto", padding: "40px 16px"}}>
            <Card bordered style={{borderRadius: 8}}>
                <NoWrap><Title level={3}>My Profile</Title></NoWrap>
                <Text type="secondary">View and update your account details</Text>
                <Divider/>
                <Form layout="vertical" form={form} onFinish={handleProfileUpdate}>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="first_name" label="First Name">
                                <Input placeholder="John"/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="last_name" label="Last Name">
                                <Input placeholder="Doe"/>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="email" label="Email">
                        <Input placeholder="you@example.com"/>
                    </Form.Item>

                    <Form.Item name="phone" label="Phone Number">
                        <Input placeholder="+1 (555) 123-4567"/>
                    </Form.Item>

                    <Form.Item name="birthday" label="Birthday">
                        <DatePicker
                            style={{width: "100%"}}
                            format="YYYY-MM-DD"
                            disabledDate={(current) => current && current > dayjs().endOf("day")}
                            placeholder="Select your birthday"
                        />
                    </Form.Item>

                    <Form.Item>
                        <Space wrap>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                Save Changes
                            </Button>
                            {justSaved && (
                                <span style={{color: "green", fontWeight: 500}}>✔ Profile updated</span>
                            )}
                            {errorMessage && (
                                <span style={{color: "red", fontWeight: 500}}>{errorMessage}</span>
                            )}
                        </Space>
                    </Form.Item>
                </Form>
            </Card>

            <Card bordered={false} style={{marginTop: 40}}>
                <Title level={4}>Change Password</Title>
                <Text type="secondary">Make sure it’s something secure</Text>
                <Divider/>
                <Form layout="vertical" form={passwordForm} onFinish={handlePasswordChange}>
                    <Form.Item
                        name="current_password"
                        label="Current Password"
                        rules={[{required: true}]}
                    >
                        <Input.Password placeholder="Current password"/>
                    </Form.Item>

                    <Form.Item
                        name="new_password"
                        label="New Password"
                        rules={[{required: true}]}
                    >
                        <Input.Password placeholder="New password"/>
                    </Form.Item>

                    <Form.Item>
                        <Space wrap>
                            <Button type="primary" htmlType="submit" loading={passwordLoading}>
                                Change Password
                            </Button>
                            {passwordSaved && (
                                <span style={{color: "green", fontWeight: 500}}>✔ Password changed</span>
                            )}
                            {passwordError && (
                                <span style={{color: "red", fontWeight: 500}}>{passwordError}</span>
                            )}
                        </Space>
                    </Form.Item>
                </Form>
            </Card>
        </div>

    );
};

export default UserProfilePage;
